import {
  type AgentDto,
  type ApprovalDto,
  can,
  type RunDto,
  type RunEventDto,
  type RunStatus,
  TERMINAL_RUN_STATUSES,
} from '@af/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { call } from './api';
import { preview } from './format';
import { useOrgEvent } from './org-events';
import { useRole } from './permissions';
import { qk, useAgents } from './queries';

/** Live operations status of an agent (agents tab, office view, context inspector). */
export type LiveStatus = 'running' | 'approval' | 'idle' | 'paused' | 'failed';

const isTerminal = (s: string) => TERMINAL_RUN_STATUSES.has(s as RunStatus);
const FAILED_FRESH_MS = 24 * 3600_000;

/**
 * Pure: an agent's live status from its runs (newest first) and pending approvals.
 * paused > awaiting approval > running > failed (latest run failed in the last 24h) > idle.
 */
export function deriveLiveStatus(
  agent: AgentDto,
  runs: RunDto[],
  pendingApprovals: number,
  now = Date.now(),
): { status: LiveStatus; run: RunDto | null } {
  const active = runs.find((r) => !isTerminal(r.status)) ?? null;
  const latest = runs[0] ?? null;
  if (agent.status !== 'active') return { status: 'paused', run: active ?? latest };
  if (pendingApprovals > 0 || active?.status === 'awaiting_approval' || active?.status === 'awaiting_human')
    return { status: 'approval', run: active ?? latest };
  if (active || (agent.stats?.activeRuns ?? 0) > 0) return { status: 'running', run: active ?? latest };
  if (latest?.status === 'failed' && now - Date.parse(latest.endedAt ?? latest.createdAt) < FAILED_FRESH_MS)
    return { status: 'failed', run: latest };
  return { status: 'idle', run: latest };
}

/** What a running agent is doing right now, in plain terms (context inspector). */
export type RunActivity =
  | { kind: 'thinking' }
  | { kind: 'writing' }
  | { kind: 'tool'; name: string }
  | { kind: 'approval'; name: string }
  | { kind: 'delegating'; role: string };

/** One-line activity snippet from run events (latest tool call or the tail of the streamed text). */
export interface RunTail {
  seq: number;
  text: string;
  tool: string | null;
  snippet: string | null;
  activity: RunActivity | null;
}

/** The run is not doing anything itself right now: it waits for someone (people, helpers, a slot, a time). */
export function isWaitingRun(run: Pick<RunDto, 'status'> | null | undefined): boolean {
  return (
    !!run &&
    ['queued', 'scheduled', 'waiting_children', 'awaiting_approval', 'awaiting_human'].includes(run.status)
  );
}

export function reduceTail(prev: RunTail | undefined, events: RunEventDto[]): RunTail {
  let { seq, text, tool, snippet, activity } = prev ?? {
    seq: 0,
    text: '',
    tool: null,
    snippet: null,
    activity: null,
  };
  for (const e of events) {
    if (e.seq <= seq) continue;
    seq = e.seq;
    const p = e.payload;
    if (e.type === 'text-delta') {
      text = (text + String(p.delta ?? '')).slice(-240);
      tool = null;
      snippet = text.replace(/\s+/g, ' ').trim().slice(-140) || snippet;
      activity = { kind: 'writing' };
    } else if (e.type === 'tool-call') {
      tool = String(p.toolName ?? 'tool');
      text = '';
      snippet = `● ${tool}(${preview(p.input, 60)})`;
      activity = { kind: 'tool', name: tool };
    } else if (e.type === 'tool-result' && tool) {
      snippet = `⎿ ${tool} ${p.isError ? 'failed' : 'done'}`;
      activity = { kind: 'thinking' };
    } else if (e.type === 'approval-requested') {
      snippet = `? waiting for approval · ${String(p.toolName ?? '')}`;
      activity = { kind: 'approval', name: String(p.toolName ?? '') };
    } else if (e.type === 'subagent-spawned') {
      snippet = `↳ spawned ${String(p.role ?? 'sub-agent')}`;
      activity = { kind: 'delegating', role: String(p.role ?? 'sub-agent') };
    } else if (e.type === 'reasoning-delta') {
      if (!snippet) snippet = '✳ Thinking…';
      activity = { kind: 'thinking' };
    }
  }
  return { seq, text, tool, snippet, activity };
}

const MAX_TAILS = 8;
const TICK_MS = 4000;
const THROTTLE_MS = 1200;

/**
 * Incremental tails for the given active runs: `runEvents?after=<seq>` (only new events), refreshed on
 * org SSE events and — while the tab is visible and something is running — on a slow tick.
 */
export function useRunTails(runIds: string[], enabled: boolean): Record<string, RunTail> {
  const [tails, setTails] = useState<Record<string, RunTail>>({});
  const tailsRef = useRef(tails);
  tailsRef.current = tails;
  const inflight = useRef(new Map<string, number>());
  const key = runIds.slice(0, MAX_TAILS).join(',');
  const ids = useMemo(() => (key ? key.split(',') : []), [key]);

  const pull = useCallback(async (id: string) => {
    const last = inflight.current.get(id) ?? 0;
    if (Date.now() - last < THROTTLE_MS) return;
    inflight.current.set(id, Date.now());
    try {
      const after = tailsRef.current[id]?.seq ?? 0;
      const events = await call('runEvents', { params: { id }, query: { after } });
      if (!events.length && tailsRef.current[id]) return;
      setTails((t) => ({ ...t, [id]: reduceTail(t[id], events) }));
    } catch {
      /* transient: the next event or tick retries */
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pull new runs when the set changes
  useEffect(() => {
    if (!enabled) return;
    for (const id of ids) void pull(id);
  }, [enabled, ids]);

  useOrgEvent((e) => {
    if (!enabled) return;
    const prefix = e.kind.split(/[.:]/)[0];
    if (!['run', 'usage', 'approval', 'agent', 'resync'].includes(prefix ?? '')) return;
    for (const id of e.runId ? ids.filter((x) => x === e.runId) : ids) void pull(id);
  });

  useEffect(() => {
    if (!enabled || !ids.length) return;
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      for (const id of ids) void pull(id);
    }, TICK_MS);
    return () => clearInterval(t);
  }, [enabled, ids, pull]);

  return tails;
}

export interface AgentLive {
  agent: AgentDto;
  status: LiveStatus;
  run: RunDto | null;
  pending: ApprovalDto[];
  snippet: string | null;
  activity: RunActivity | null;
  /** Names of the agents whose delegated runs this agent's run is waiting for (`waiting_children`). */
  waitingOn: string[];
}

/** Every agent with its live status, current run and activity snippet. */
export function useAgentsLive(opts: { tails?: boolean } = {}) {
  const role = useRole();
  const agents = useAgents();
  const canRuns = can(role, 'run:read');
  const canApprovals = can(role, 'approval:read');
  // No refetchInterval: the org SSE stream invalidates ['runs'] / ['approvals'] on change.
  const runs = useQuery({
    queryKey: qk.runs({ scope: 'live' }),
    queryFn: () => call('listRuns', { query: { limit: 200 } }),
    enabled: canRuns,
  });
  const approvals = useQuery({
    queryKey: qk.approvals('pending'),
    queryFn: () => call('listApprovals', { query: { status: 'pending' } }),
    enabled: canApprovals,
  });

  const base = useMemo(() => {
    const byAgent = new Map<string, RunDto[]>();
    for (const r of [...(runs.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt))) {
      byAgent.set(r.agentId, [...(byAgent.get(r.agentId) ?? []), r]);
    }
    return (agents.data ?? [])
      .filter((a) => a.status !== 'archived')
      .map((agent) => {
        const pending = (approvals.data ?? []).filter((x) => x.agentId === agent.id);
        const { status, run } = deriveLiveStatus(agent, byAgent.get(agent.id) ?? [], pending.length);
        return { agent, status, run, pending };
      });
  }, [agents.data, runs.data, approvals.data]);

  const activeIds = base
    .filter((x) => x.run && !isTerminal(x.run.status) && !isWaitingRun(x.run))
    .map((x) => x.run!.id);
  const tails = useRunTails(activeIds, !!opts.tails && canRuns);
  const waitingOn = useMemo(() => {
    const names = new Map((agents.data ?? []).map((a) => [a.id, a.name]));
    const out = new Map<string, string[]>();
    for (const r of runs.data ?? [])
      if (r.parentRunId && !isTerminal(r.status))
        out.set(r.parentRunId, [...(out.get(r.parentRunId) ?? []), names.get(r.agentId) ?? '?']);
    return out;
  }, [agents.data, runs.data]);

  const list: AgentLive[] = base.map((x) => ({
    ...x,
    snippet: x.run ? (tails[x.run.id]?.snippet ?? null) : null,
    activity: x.run ? (tails[x.run.id]?.activity ?? null) : null,
    waitingOn: x.run?.status === 'waiting_children' ? [...new Set(waitingOn.get(x.run.id) ?? [])] : [],
  }));
  return {
    list,
    /** Every run the hook knows (newest first is not guaranteed). */
    runs: runs.data ?? [],
    isPending: agents.isPending,
    error: agents.error ?? runs.error,
  };
}

/** Elapsed time since `iso` as `1m 08s` / `2h 05m`, re-rendered every second while mounted. */
export function useNow(intervalMs = 1000, enabled = true) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs, enabled]);
  return now;
}

export function elapsed(iso: string | null | undefined, now: number): string {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((now - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}
